import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "../primitives/form";
import { Input } from "../primitives/input";

type ColocaInputFieldProps = {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Visible label rendered above the input. Omit only for search bars or inputs with an obvious placeholder. */
  label?: ReactNode;
  /**
   * Helper text rendered below the input. Use for format hints (e.g. "Include country code") or constraints.
   * Do not repeat the label.
   */
  description?: ReactNode;
  placeholder?: string;
  type?: "text" | "email" | "password" | "number" | "date" | "time";
  inputMode?: "text" | "numeric" | "decimal" | "tel" | "email" | "url" | "search" | "none";
  maxLength?: number;
  autoComplete?: string;
  disabled?: boolean;
  /**
   * Marks the field as required: an asterisk on the label and `aria-required` on the input. Presentational —
   * the Zod schema still owns validation, and the native attribute is deliberately never emitted.
   */
  required?: boolean;
  /** Sanitizes/formats the raw input value before it is stored in the form state (e.g. digits-only). */
  transform?: (raw: string) => string;
};

/**
 * Controlled text input wired to the nearest React Hook Form context.
 *
 * Use inside a `<Form>` (i.e. a component that calls `useForm` and renders `<FormProvider>`) for any
 * single-line text, email, password, or numeric field. Validation errors from the Zod schema are displayed
 * automatically below the input via `<FormMessage>`.
 *
 * For multi-line input use a textarea field. For selects or comboboxes use the corresponding Coloca form
 * primitives instead.
 */
// `required` is destructured rather than left in `...rest` on purpose: spread onto the input it would become
// the NATIVE required attribute and hand validation to the browser, racing the Zod resolver.
export function ColocaInputField({
  name,
  label,
  description,
  required,
  transform,
  ...rest
}: ColocaInputFieldProps) {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem required={required}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <Input
              {...rest}
              {...field}
              onChange={transform ? (e) => field.onChange(transform(e.target.value)) : field.onChange}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
